// iter_302 · 学習する（4ステップの案内つき手順）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { MagnifyingGlass, Plus, ArrowRight, ArrowLeft, Check, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  API, LEARN, authHeader, detailOf, Panel, Pill, Empty, Help, Steps, Collapse,
  Spinner, STATE_JA, ROLE_JA, ENTITY_JA,
} from './shared';
import Annotate from './Annotate';
import { Correct, Submit } from './TeachCorrect';

const STEP_LABELS = ['書類', '読み取り', '修正', '完了'];

const SOURCES = [
  { key: 'dms', title: '既存の書類から選ぶ',
    text: 'ドキュメントに保存されている書類を、学習用の例として使用します。' },
  { key: 'upload', title: '新しいファイルを追加',
    text: '学習用の書類を追加します。追加しただけでは学習内容として有効になりません。',
    link: '/documents', linkLabel: 'ドキュメントで追加する' },
  { key: 'nas', title: 'NASから選ぶ',
    text: '承認されたNASフォルダーから書類を選びます。元のファイルは変更しません。',
    link: '/settings/nas-exclusions', linkLabel: 'NASの対象フォルダーを確認' },
];

// ── ステップ1 ───────────────────────────────────────────────────────────
function SelectDocument({ examples, pending, onPick, onAdded }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState('dms');

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/dms/documents`,
        { params: { q: q.trim(), limit: 10 }, headers: authHeader() });
      const items = r.data?.items || r.data || [];
      setHits(items.slice(0, 10));
      if (!items.length) toast.info('該当する書類がありません');
    } catch (e) { toast.error(detailOf(e, '書類を検索できませんでした')); } finally { setBusy(false); }
  };

  const add = async (documentId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${LEARN}/examples`, { document_id: documentId },
        { headers: authHeader() });
      toast.success('学習用書類に追加しました。追加だけでは読み取り方法は変わりません。');
      setHits([]); setQ('');
      onAdded(r.data.row);
    } catch (e) { toast.error(detailOf(e, '追加できませんでした')); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Panel title="書類を選ぶ" testId="teach-step1"
        hint="例として使う書類を選びます。元のファイルは変更しません。">
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
          {SOURCES.map((s) => (
            <button key={s.key} type="button" onClick={() => setSource(s.key)}
              className={`min-h-[96px] rounded-md border px-3 py-2 text-left transition-colors duration-200 ${
                source === s.key ? 'border-teal-700 bg-teal-50/60' : 'border-zinc-200 hover:bg-zinc-50'}`}
              data-testid={`teach-source-${s.key}`}>
              <p className="text-[12.5px] font-medium text-zinc-900">{s.title}</p>
              <p className="pt-1 text-[11.5px] leading-relaxed text-zinc-600">{s.text}</p>
            </button>
          ))}
        </div>

        {source === 'dms' ? (
          <div className="pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="図番・書類名で探す" aria-label="書類を探す"
                className="h-10 w-64 rounded-md border border-zinc-200 px-2 text-[12.5px]"
                data-testid="teach-search-input" />
              <Button variant="outline" className="h-10 text-[12px]" onClick={search} disabled={busy}
                data-testid="teach-search-btn">
                <MagnifyingGlass size={13} className="mr-1" />探す
              </Button>
            </div>
            <div className="space-y-1.5 pt-2" data-testid="teach-hits">
              {hits.map((h) => (
                <div key={h.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2">
                  <span className="min-w-0 truncate text-[12.5px] text-zinc-800">
                    {h.title || h.document_number || h.id}
                  </span>
                  <Button size="sm" className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
                    onClick={() => add(h.id)} disabled={busy}
                    data-testid={`teach-add-${h.id}`}>
                    <Plus size={12} className="mr-1" />学習用に追加
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="pt-3">
            <p className="text-[12px] text-zinc-600">{SOURCES.find((s) => s.key === source).text}</p>
            <a href={SOURCES.find((s) => s.key === source).link}
              className="mt-2 inline-flex min-h-[36px] items-center text-[12px] text-teal-700 hover:underline"
              data-testid={`teach-source-link-${source}`}>
              {SOURCES.find((s) => s.key === source).linkLabel} →
            </a>
            <p className="pt-2 text-[11.5px] text-zinc-500">
              追加した書類は、ここの「既存の書類から選ぶ」で学習用の例として選べます。
            </p>
          </div>
        )}
      </Panel>

      <Panel title="登録済みの学習用書類" testId="teach-examples"
        hint="続けて読み取り結果を確認できます。">
        {(pending || []).length > 0 && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50/40 p-3"
            data-testid="teach-pending-documents">
            <p className="text-[12px] font-medium text-zinc-800">
              確認待ちの書類（読み取り結果の確認が終わっていません）
            </p>
            <div className="space-y-1.5 pt-2">
              {pending.map((p) => (
                <div key={p.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-3 py-2"
                  data-testid={`teach-pending-${p.id}`}>
                  <span className="min-w-0 truncate text-[12.5px] text-zinc-800">
                    {p.title || p.id}
                  </span>
                  <Button size="sm" className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
                    onClick={() => add(p.id)} disabled={busy}
                    data-testid={`teach-pending-add-${p.id}`}>
                    <Plus size={12} className="mr-1" />学習用に追加して確認
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        {examples.length === 0 ? (
          <Empty testId="teach-examples-empty">
            {'学習用の書類はまだありません。\n実際の書類を追加し、読み取り結果を確認してください。'}
          </Empty>
        ) : (
          <div className="space-y-1.5">
            {examples.map((e) => (
              <div key={e.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2"
                data-testid={`teach-example-${e.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] text-zinc-900">
                    {e.document_title || e.document_id}
                    <span className="ml-2"><Pill tone={e.status === 'approved' ? 'teal' : e.status === 'stale' ? 'amber' : 'zinc'}>
                      {e.status_label_ja}
                    </Pill></span>
                  </p>
                  {e.stale_reason && <p className="text-[11px] text-amber-700">{e.stale_reason}</p>}
                </div>
                <Button size="sm" className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
                  onClick={() => onPick(e)} data-testid={`teach-open-${e.id}`}>
                  読み取り結果を確認<ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ── ステップ2 ───────────────────────────────────────────────────────────
function ReviewReading({ data, onBack, onNext }) {
  const roles = Object.keys(ROLE_JA);
  return (
    <div className="space-y-4">
      <Panel title="読み取り結果を確認" testId="teach-step2"
        hint="読み取った内容と、その根拠になった場所を確認します。">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="rounded-md border border-zinc-200 p-3" data-testid="teach-source-preview">
            <p className="text-[12px] font-medium text-zinc-800">書類の内容</p>
            <p className="pt-1 text-[11.5px] text-zinc-500">
              {data.document.title || data.document.id}
            </p>
            <div className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-zinc-50 p-2 text-[11px] leading-relaxed text-zinc-700">
              {(data.region_texts || {}).title_block || Object.values(data.region_texts || {})[0]
                || '読み取れた文字がありません'}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              <Pill tone="teal">{data.profile_label || data.profile_key || '種類不明'}</Pill>
              {data.document_subtype && <Pill>{data.document_subtype}</Pill>}
            </div>
            <div className="space-y-1.5" data-testid="teach-result-fields">
              {roles.map((role) => {
                const stamp = (data.stamp_candidates || {})[role];
                const src = (data.field_sources || {})[role] || {};
                const state = (data.field_states || {})[role];
                if (!stamp && !src.title_block_field && !state) return null;
                const entity = stamp?.entity_type || src.entity_type;
                return (
                  <div key={role} className="rounded-md border border-zinc-200 px-3 py-2"
                    data-testid={`teach-field-${role}`}>
                    <p className="text-[12.5px] font-medium text-zinc-900">
                      {ROLE_JA[role]}
                      <span className="ml-2 text-[11.5px] text-zinc-600">
                        {(data.raw || {})[role] || stamp?.candidate || '（候補なし）'}
                      </span>
                    </p>
                    <p className="flex flex-wrap gap-1.5 pt-1">
                      {state && <Pill tone={state === 'confirmed' ? 'teal' : state === 'unreadable' ? 'amber' : 'zinc'}
                        testId={`teach-state-${role}`}>{STATE_JA[state] || state}</Pill>}
                      {stamp?.stamp_type_label_ja && <Pill>押印の種類: {stamp.stamp_type_label_ja}</Pill>}
                      {entity && <Pill tone={entity === 'department' ? 'amber' : 'zinc'}
                        testId={`teach-entity-${role}`}>種類: {ENTITY_JA[entity] || entity}</Pill>}
                      {stamp?.date_candidate && <Pill>日付: {stamp.date_candidate}</Pill>}
                      {stamp?.conflict && <Pill tone="rose">押印の重なり</Pill>}
                    </p>
                    {(stamp?.reason_ja || src.reason_ja) && (
                      <p className="pt-1 text-[11px] text-zinc-500">
                        読み取り元: {src.title_block_field || '—'} · 理由: {stamp?.reason_ja || src.reason_ja}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="pt-3">
          <Collapse label="詳細診断（読み取り文字と領域）" testId="teach-diagnostics">
            <div className="space-y-2">
              {Object.entries(data.region_structure || {}).map(([k, v]) => (
                <p key={k} className="text-[11px] text-zinc-600" data-testid={`teach-region-${k}`}>
                  {k}: {v.line_count}行 / 確度 {v.confidence}
                </p>
              ))}
              {Object.entries(data.region_texts || {}).map(([k, v]) => (
                <div key={k}>
                  <p className="text-[11px] font-medium text-zinc-700">{k}</p>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-zinc-50 p-2 text-[10.5px] text-zinc-600">{v}</pre>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
          <Button variant="outline" className="h-10 text-[12px]" onClick={onBack}
            data-testid="teach-step2-back">
            <ArrowLeft size={13} className="mr-1" />書類を選び直す
          </Button>
          <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800" onClick={onNext}
            data-testid="teach-step2-next">
            次へ<ArrowRight size={13} className="ml-1" />
          </Button>
        </div>
      </Panel>
    </div>
  );
}

// ── 学習する（全体） ────────────────────────────────────────────────────
export default function Teach({ onGo, onChanged }) {
  const [step, setStep] = useState(1);
  const [examples, setExamples] = useState([]);
  const [pending, setPending] = useState([]);
  const [data, setData] = useState(null);
  const [session, setSession] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadExamples = useCallback(async () => {
    try {
      const r = await axios.get(`${LEARN}/examples`, { headers: authHeader() });
      setExamples(r.data?.rows || []);
      const o = await axios.get(`${LEARN}/overview`, { headers: authHeader() });
      const known = new Set((r.data?.rows || []).map((x) => x.document_id));
      setPending((o.data?.pending_documents || []).filter((p) => !known.has(p.id)));
    } catch (e) { toast.error(detailOf(e, '学習用書類を取得できませんでした')); }
  }, []);
  useEffect(() => { loadExamples(); }, [loadExamples]);

  const reloadSession = useCallback(async (sid) => {
    const id = sid || session?.teaching_session_id;
    if (!id) return;
    try {
      const r = await axios.get(`${LEARN}/teaching/sessions/${id}`,
        { headers: authHeader() });
      setSession(r.data.session);
      setMeta((m) => ({ ...(m || {}), real_document_status: r.data.real_document_status }));
    } catch (e) { toast.error(detailOf(e, '教示セッションを取得できませんでした')); }
  }, [session]);

  const open = async (example) => {
    setLoading(true);
    try {
      const r = await axios.get(`${LEARN}/examples/${example.id}/review`,
        { headers: authHeader() });
      setData(r.data);
      const t = await axios.post(`${LEARN}/teaching/sessions`,
        { document_id: r.data.document.id }, { headers: authHeader() });
      setSession(t.data.session);
      setMeta({ region_roles: t.data.region_roles,
        correction_types: t.data.correction_types,
        real_document_status: t.data.real_document_status });
      setStep(2);
    } catch (e) {
      toast.error(detailOf(e, '読み取り結果を取得できませんでした'));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Steps step={step} labels={STEP_LABELS} testId="teach-steps" />
      {loading && <Spinner />}
      {step === 1 && (
        <SelectDocument examples={examples} pending={pending} onPick={open}
          onAdded={async (row) => { await loadExamples(); onChanged?.(); open(row); }} />
      )}
      {step === 2 && data && session && (
        <>
          <ReviewReading data={data} onBack={() => setStep(1)} onNext={() => setStep(3)} />
          <Annotate session={session} meta={meta} onChanged={() => reloadSession()} />
        </>
      )}
      {step === 3 && data && session && (
        <Correct session={session} meta={meta} data={data}
          onChanged={() => reloadSession()}
          onBack={() => setStep(2)} onNext={() => setStep(4)} />
      )}
      {step === 4 && data && session && (
        <Submit session={session} onGo={onGo} onBack={() => setStep(3)}
          onDone={async () => {
            await loadExamples(); onChanged?.(); setSession(null); setStep(1);
          }} />
      )}
      <Help label="学習用書類" testId="teach-help-example">
        今後の読み取りを確認するための例として使う書類です。追加しただけでは、システム全体の読み取り方法は変わりません。
      </Help>
    </div>
  );
}
